"use client";

import React from "react";
import { motion } from "framer-motion";
import Tag from "../../Components/ui/tag";
import AnimatedIcon from "../../Components/ui/animated-icon";
import siteData from "../../data/site-data";
import { IoSparkles, IoMail } from "react-icons/io5";
import {
  staggerContainer,
  staggerItem,
  fadeInUp,
  textReveal,
  buttonHover,
  bounceIn,
  pulse,
  cardHover,
} from "../../lib/animations";
import { useRouter } from "next/navigation";



interface ModalBullet {
  label?: string;
  heading: string;
}

const bulletsData = [
  {
    label: "Client-Centric Approach",
    heading:
      " – We listen first, build later. Your business needs are at the core of everything we design.",
  },
  {
    label: "Agile & Transparent",
    heading:
      " – Flexible processes, fast iterations, and complete visibility at every stage.",
  },
  {
    label: "Full-Stack Expertise",
    heading:
      " – From MERN stack to AI/ML, we bring deep technical knowledge to every project.",
  },
  {
    label: "Quality Without Compromise",
    heading:
      " – We don't just build products—we deliver scalable, secure, and high-performing solutions.",
  },
];

const whatWeDo = [
  { heading: "Custom Web & Mobile App Development" },
  { heading: "UI/UX Design & Product Prototyping" },
  { heading: "Enterprise CRM/ERP Solutions" },
  { heading: "AI & Machine Learning Integration" },
  { heading: "Cloud Infrastructure & DevOps" },
  { heading: "CMS, eCommerce & PWA Solutions" },
];

const renderBullets = (bullets: ModalBullet[]) => (
  <ul className="space-y-4 mt-6">
    {bullets.map((bullet: any, idx: number) => (
      <motion.li
        key={idx}
        className="text-text text-base flex items-start gap-3 group cursor-pointer"
        whileHover={{ x: 5 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="text-primary mt-1 flex-shrink-0"
          whileHover={{ x: 2 }}
          transition={{ duration: 0.2 }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-4 h-4"
          >
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </motion.div>
        <div>
          <strong className="text-foreground">{bullet.label}</strong>
          {bullet.heading}
        </div>
      </motion.li>
    ))}
  </ul>
);

export default function AboutUs() {
  const router = useRouter();
  const { hero, industryMegaMenu } = siteData;

  return (
    <motion.div
      className="min-h-screen bg-light"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Hero Section */}
      <motion.section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden bg-cover bg-center bg-no-repeat rounded-2xl"
            style={{ backgroundImage: "url('/images/about-bg.jpg')" }}
          >
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-black/70"></div>

            <div className="relative z-10 py-20 px-8 text-center">
              <motion.div className="mb-8" variants={fadeInUp}>
                <Tag
                  icon={
                    <AnimatedIcon animation="pulse" className="text-primary">
                      <IoSparkles />
                    </AnimatedIcon>
                  }
                  text={hero.tag.text}
                  bgColor="light"
                  textColor="primary"
                  borderColor="secondary"
                />
              </motion.div>

              <motion.h1
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight"
                variants={textReveal}
              >
                About ZySoftec
              </motion.h1>

              <motion.p
                className="text-base md:text-lg text-gray-200 mb-8 max-w-5xl mx-auto leading-relaxed"
                variants={textReveal}
              >
                ZySoftec is a forward-thinking software development company
                committed to delivering innovative, scalable, and future-ready
                digital solutions. We partner with startups, enterprises, and
                agencies to craft custom software products that solve real-world
                challenges and drive business growth.
              </motion.p>

              <motion.p
                className="text-base md:text-lg text-gray-200 mb-8 max-w-5xl mx-auto leading-relaxed"
                variants={textReveal}
              >
                Founded with a mission to simplify technology and amplify
                business performance, ZySoftec blends creativity with
                cutting-edge engineering to build impactful web apps, mobile
                apps, AI-powered tools, and enterprise systems. We believe in
                turning ideas into intelligent digital experiences—secure, fast,
                and user-centric.
              </motion.p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Vision & Mission Section */}
      <motion.section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid md:grid-cols-2 gap-8"
            variants={staggerContainer}
          >
            <motion.div
              className="bg-light rounded-xl p-8 border border-gray-200"
              variants={staggerItem}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.1 }}
            >
              <motion.h2
                className="text-2xl md:text-3xl font-bold text-foreground mb-4"
                variants={textReveal}
              >
                Our Vision
              </motion.h2>
              <motion.p
                className="text-base text-text leading-relaxed"
                variants={textReveal}
              >
                To be a global leader in digital innovation, helping brands grow
                smarter, move faster, and operate more efficiently through
                transformative software solutions.
              </motion.p>
            </motion.div>

            <motion.div
              className="bg-light rounded-xl p-8 border border-gray-200"
              variants={staggerItem}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.1 }}
            >
              <motion.h2
                className="text-2xl md:text-3xl font-bold text-foreground mb-4"
                variants={textReveal}
              >
                Our Mission
              </motion.h2>
              <motion.p
                className="text-base text-text leading-relaxed"
                variants={textReveal}
              >
                To empower businesses through cutting-edge technology,
                exceptional design, and result-driven strategies, creating
                digital products that are intuitive, innovative, and impactful.
              </motion.p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* What We Do Section */}
      <motion.section className="py-16 bg-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={staggerContainer}>
            <motion.h2
              className="text-2xl md:text-3xl font-bold text-foreground mb-6 text-center"
              variants={staggerItem}
            >
              What We Do
            </motion.h2>
            <motion.p
              className="text-base md:text-lg text-text mb-8 text-center leading-relaxed"
              variants={textReveal}
            >
              From concept to code, design to deployment, we offer end-to-end
              development services including:
            </motion.p>
            <motion.div variants={staggerItem}>
              {renderBullets(whatWeDo)}
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Who We Serve Section */}
      <motion.section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={staggerContainer}>
            <motion.h2
              className="text-2xl md:text-3xl font-bold text-foreground mb-6 text-center"
              variants={staggerItem}
            >
              Who We Serve
            </motion.h2>
            <motion.p
              className="text-base md:text-lg text-text mb-8 text-center leading-relaxed"
              variants={textReveal}
            >
              ZySoftec works across diverse industries, including:
            </motion.p>

            <motion.div
              className="grid gap-4 md:grid-cols-3 lg:grid-cols-5 grid-cols-2 mb-8"
              variants={staggerContainer}
            >
              {industryMegaMenu.categories[0].items.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="group cursor-pointer"
                  variants={staggerItem}
                  custom={index}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-2 text-text text-base p-3 rounded-lg hover:bg-light transition-colors">
                    <motion.div
                      className="text-primary flex-shrink-0"
                      whileHover={{ x: 2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-4 h-4"
                      >
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                      </svg>
                    </motion.div>
                    <span className="font-medium">{item.title}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.p
              className="text-base md:text-lg text-text text-center leading-relaxed"
              variants={textReveal}
            >
              Whether you're launching a SaaS product, optimizing workflows with
              automation, or enhancing user experience through smart design,
              ZySoftec is your technology partner every step of the way.
            </motion.p>
          </motion.div>
        </div>
      </motion.section>

      {/* Why Choose Us Section */}
      <motion.section className="py-16 bg-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={staggerContainer}>
            <motion.h2
              className="text-2xl md:text-3xl font-bold text-foreground mb-6 text-center"
              variants={staggerItem}
            >
              Why Choose ZYSOFTEC?
            </motion.h2>
            <motion.div variants={staggerItem}>
              {renderBullets(bulletsData)}
            </motion.div>

            <motion.div className="text-center mt-12" variants={staggerItem}>
              <motion.p
                className="text-base md:text-lg font-medium text-text mb-6 leading-relaxed"
                variants={textReveal}
              >
                Let's build something remarkable together.
              </motion.p>
              <motion.div
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#EE746D] text-foreground font-medium rounded-md hover:border-primary hover:bg-white transition-colors text-sm cursor-pointer"
                variants={staggerItem}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                onClick={() => router.push("/contact")}
              >
                {hero.buttons[1].text}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </motion.div>
  );
}
